import { array, codec, nullish, object, optional, unknown, url } from "zod";
import { MealRow } from "./meal-row.js";
import { NonEmptyString } from "./non-empty-string.js";
import { coerceNullToUndefined } from "./coercions.js";
/** @import { input, output } from "zod" */

const mealSchema = object({
  description: nullish(NonEmptyString).transform(coerceNullToUndefined),
  ingredients: nullish(array(NonEmptyString)).transform(coerceNullToUndefined),
  meta: optional(unknown()),
  name: NonEmptyString,
  photoURL: nullish(url()).transform(coerceNullToUndefined),
  photoURLs: nullish(array(url())).transform(coerceNullToUndefined),
  recipeURL: nullish(url()).transform(coerceNullToUndefined),
  source: nullish(NonEmptyString).transform(coerceNullToUndefined),
});

const { decode: decodeMealRow, parse: parseMealRow } = codec(MealRow, mealSchema, {
  decode: (mealRow) => {
    const mealName = mealRow[1];

    if (mealRow[0]) console.debug(`Photo for ${mealName} was `, mealRow[0]);

    const photoURL = url().safeParse(mealRow[0]).data;
    const recipeURL = url().safeParse(mealRow[4]).data;
    if (mealRow[4] && !recipeURL) console.debug(`Found source that wasn't meal URL: ${mealRow[4]}`);

    return {
      ingredients: mealRow[3] ? mealRow[3].split(",").map((i) => i.trim()) : undefined,
      meta: { googleSheetsRow: mealRow },
      name: mealName,
      photoURL,
      recipeURL,
      source: mealRow[4],
    };
  },
  encode: (meal) => {
    /** @type {MealRow} */
    const mealRow = [
      meal.photoURL ?? meal.photoURLs?.at(0) ?? undefined,
      meal.name,
      undefined,
      meal.ingredients?.join(", "),
      meal.source ?? undefined,
      undefined,
      undefined,
    ];

    return mealRow;
  },
});

export const Meal = Object.assign(mealSchema, {
  /**
   * Get a {@link Meal} object from a given {@link MealRow}
   *
   * @param {input<typeof MealRow>} givenRow
   * @returns {Meal}
   */
  fromRow(givenRow) {
    return decodeMealRow(givenRow);
  },
});
/** @typedef {output<typeof Meal>} Meal */
